import { Member, TimeEntry } from '__generated__/graphql';
import { OUT_SIDE_PROJECT_TIME_ENTRIES_QUERY } from 'apollo/queries/time-entry-queries';
import { useApolloPaging } from 'hooks';
import useEmployeeNameFormatter from 'hooks/ui/useEmployeeNameFormatter';
import { t } from 'i18next';
import { capitalize, isNil, orderBy } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import ICursorable from 'types/Cursorable';
import ITimeRange from 'types/TimeRange';
import TimeRangeType from 'types/TimeRangeType';
import DateTimeFormat from 'utils/constants/dateTimeFormat';
import { dateTimeFromISOWithoutZone } from 'utils/dateUtils';
import { getMaxDistance } from 'utils/locationUtils';
import { getFormattedPathFromProject } from 'utils/projectUtils';
import { numericFirst } from 'utils/stringUtils';
import { StatusOptions } from '../GpsOutsideProjectReportSidePanel/GpsOutsideProjectReportSidePanel';

export interface IGpsOutsideProjectReportFilter {
  memberId: string | null;
  employeeGroupId: string | null;
  currentTimeRangeType: TimeRangeType | null | undefined;
  timeRange: ITimeRange<DateTime | null> | null | undefined;
  reviewdByMemberId: string | null;
  projectId: string | null;
  projectGroupId: string | null;
  status: string | null;
}

export interface IGpsOutsideProjectReportRowItem {
  id: string;
  memberName: string;
  memberId: string;
  timeEntry: TimeEntry;
  status: string;
  project: string;
  projectId: string;
  maxDistance: string;
  reviewedBy: { name: string; date: string };
  comments: string;
  member: Member;
  memberGroup: string;
  projectGroup: string;
}

export interface IGpsOutsideProjectReportData {
  data: IGpsOutsideProjectReportRowItem[];
  pendingReview: number;
  authorized: number;
  unauthorized: number;
}

export default function useGpsOutsideProjectReportData(filter: IGpsOutsideProjectReportFilter) {
  const { getAll } = useApolloPaging();
  const nameFormatter = useEmployeeNameFormatter();

  return useCallback(async () => {
    const timeEntryData = getAll<TimeEntry & ICursorable>('timeEntries', {
      query: OUT_SIDE_PROJECT_TIME_ENTRIES_QUERY,
      variables: {
        first: 200,
        filter: {
          flaggedTimeEntryLocation: {
            status: filter.status
              ? filter.status === StatusOptions.PENDING_REVIEW
                ? { isNull: true }
                : { equal: filter.status }
              : undefined,
            memberId: filter.memberId ? { equal: filter.memberId } : undefined,
            member: filter.employeeGroupId ? { memberGroupId: { equal: filter.employeeGroupId } } : undefined,
            timeEntry: {
              projectId: { isNull: false },
              project: {
                idWithDescendants: !isNil(filter.projectId) ? { contains: [filter.projectId] } : undefined,
                projectGroupId: filter.projectGroupId ? { equal: filter.projectGroupId } : undefined,
              },
              deletedOn: { isNull: true },
            },
            reviewedByMemberId: filter.reviewdByMemberId ? { equal: filter.reviewdByMemberId } : undefined,
            deletedOn: { isNull: true },
            createdOn: !isNil(filter.timeRange)
              ? {
                  between: {
                    start: filter.timeRange?.startTime
                      ?.minus(filter.timeRange?.startTime.toLocal().offset * 60 * 1000)
                      .toISO({ suppressSeconds: true }),
                    end: filter.timeRange?.endTime
                      ?.minus(filter.timeRange?.endTime.toLocal().offset * 60 * 1000)
                      .toISO({ suppressSeconds: true }),
                  },
                }
              : undefined,
          },
        },
      },
      fetchPolicy: 'network-only',
    });

    return await Promise.all([timeEntryData]).then((queryResults) => {
      const [res] = queryResults;

      const reportData = res.map((timeEntry: TimeEntry) => timeEntry);

      const data: IGpsOutsideProjectReportRowItem[] = reportData
        .map((timeEntry: TimeEntry) => {
          if (!timeEntry.flaggedTimeEntryLocation) return undefined;

          const flaggedLocation = timeEntry.flaggedTimeEntryLocation;
          const member = flaggedLocation.member;
          const status = flaggedLocation.status;
          const reviewedByMember = flaggedLocation.reviewedByMember;
          const distance = getMaxDistance(flaggedLocation, timeEntry.project);
          if (distance === '---') return undefined;

          return {
            id: timeEntry.flaggedTimeEntryLocation.id,
            memberName: nameFormatter(member.firstName ?? '', member.lastName ?? ''),
            memberId: member.id,
            timeEntry: timeEntry,
            status: status ? capitalize(status) : t('Pending Review'),
            project: timeEntry.project ? getFormattedPathFromProject(timeEntry.project, true) : '---',
            projectId: timeEntry.project ? timeEntry.project?.id : '---',
            maxDistance: distance ?? '---',
            reviewedBy: {
              name: nameFormatter(reviewedByMember?.firstName ?? '', reviewedByMember?.lastName ?? '---'),
              date: flaggedLocation.lastStatusUpdatedOn
                ? dateTimeFromISOWithoutZone(flaggedLocation.lastStatusUpdatedOn)
                    .toLocal()
                    .toFormat(DateTimeFormat.dateAtTime)
                : '',
            },
            comments: flaggedLocation.comment ?? '---',
            member: flaggedLocation.member,
            memberGroup: flaggedLocation.member.memberGroup?.groupName ?? '—',
            projectGroup: timeEntry.project?.projectGroup?.groupName ?? '—',
          };
        })
        .filter((item): item is IGpsOutsideProjectReportRowItem => !!item);

      const filteredData = orderBy(
        data,
        [(item) => numericFirst(item.memberName), (item) => item.timeEntry.startTime],
        ['asc', 'desc']
      );

      return { data: filteredData } as IGpsOutsideProjectReportData;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filter.memberId,
    filter.employeeGroupId,
    filter.projectId,
    filter.projectGroupId,
    filter.reviewdByMemberId,
    filter.status,
    filter.timeRange?.endTime,
    filter.timeRange?.startTime,
    filter.currentTimeRangeType,
    getAll,
    nameFormatter,
  ]);
}
